import { anomalyFeature, anomalyFeatureEntitlement, } from './anomaly-features.js';
/**
 * The anomaly families — what a detector is looking FOR, as opposed to the
 * measurements it reads.
 *
 * A family is the unit a tenant configures, a budget share is allocated to, and
 * an incident is attributed by. It sits above the feature registry: several
 * families can read one feature, and one family can read several.
 *
 * ⚠️ **Only the families the detector can actually produce today.** The design
 * catalogues fifteen; six are buildable from the Release 1 feature set and the
 * rest need position, CAN or tachograph inputs that no detector reads yet.
 * Declaring a family nothing computes is the "declared and not built" fault this
 * programme has recorded three times — and here it would be worse than usual,
 * because a family with a budget share consumes allocation from the ones that
 * work.
 */
/**
 * The canonical seven incident types, MIRRORED.
 *
 * ⚠️ `INCIDENT_TYPES` lives in `@oip/domain` and the two packages deliberately
 * do not depend on each other, so a copy is unavoidable — this is the same seam
 * `POLICY_EVENT_TYPES` already crosses. What is avoidable is an UNGUARDED copy:
 * `anomaly-families.test.ts` reads the domain source as TEXT and fails when the
 * two disagree, exactly as `policy-event-types.test.ts` does for event types.
 *
 * There was already one hand-copy of this list in the query catalogue. A third
 * free-floating one is how the first becomes wrong without anybody noticing.
 */
export const CANONICAL_INCIDENT_TYPES = [
    'HIGH_RISK_DRIVER_BEHAVIOUR',
    'COMPLIANCE_RISK',
    'ASSET_AVAILABILITY_RISK',
    'VEHICLE_HEALTH_RISK',
    'FUEL_LOSS_RISK',
    'SAFETY_VIOLATION_RISK',
    'SECURITY_RISK',
];
/**
 * The detection outcomes, MIRRORED from `DETECTION_OUTCOMES` in `@oip/domain`.
 *
 * ⚠️ The two packages deliberately do not depend on each other, so a copy is
 * unavoidable — the same seam `POLICY_EVENT_TYPES` crosses. What is avoidable is
 * an UNGUARDED copy: `anomaly-families.test.ts` reads the domain source as TEXT
 * and fails when the two disagree. A missing value here would render a real
 * outcome as an unrecognised one on the only screen that shows them.
 */
/**
 * The review verdicts, MIRRORED from `ANOMALY_VERDICTS` in `@oip/domain`.
 *
 * ⚠️ Same seam and same guard as the outcomes below: the packages do not depend
 * on each other, so the copy is unavoidable and `anomaly-families.test.ts` reads
 * the domain source as TEXT to keep it honest. A missing value here would make a
 * verdict the platform records unsubmittable through its own API — and the
 * denominator of every precision figure is two of these four, so a drift in
 * either direction changes what the detector is reported to be worth.
 */
export const ANOMALY_VERDICTS_MIRROR = [
    'CONFIRMED_ISSUE',
    'EXPECTED_BEHAVIOUR',
    'INSUFFICIENT_INFORMATION',
    'FALSE_POSITIVE',
];
export const DETECTION_OUTCOMES_MIRROR = [
    'PUBLISHABLE',
    'NORMAL',
    'UNDECIDABLE',
    'NO_BASELINE',
    'GATED_RELIABILITY',
    'DEGRADED_INPUT',
    'PLATFORM_HEALTH',
];
export const ANOMALY_LATENCY_CLASSES = ['NEAR_REAL_TIME', 'HOURLY', 'DAILY'];
export const ANOMALY_FAMILIES = {
    DISTANCE: {
        label: 'Distance',
        description: 'A day’s distance far above or below what this vehicle usually covers.',
        features: ['vehicle.daily.distanceKm'],
        incidentType: 'ASSET_AVAILABILITY_RISK',
        latency: 'DAILY',
        defaultImpact: 'MEDIUM',
        budgetShare: 0.2,
        action: 'Confirm whether the journey was planned; check for unauthorised use.',
    },
    OPERATING_TIME: {
        label: 'Operating time',
        description: 'Movement starting or ending at hours this vehicle does not usually work.',
        features: ['vehicle.daily.operatingStartMinute', 'vehicle.daily.operatingEndMinute'],
        incidentType: 'COMPLIANCE_RISK',
        latency: 'DAILY',
        defaultImpact: 'MEDIUM',
        budgetShare: 0.15,
        action: 'Confirm out-of-hours authorisation; check the driver assignment.',
    },
    DRIVE_DURATION: {
        label: 'Driving time',
        description: 'Time spent driving materially above this vehicle’s normal.',
        features: ['vehicle.daily.driveMinutes'],
        incidentType: 'COMPLIANCE_RISK',
        latency: 'DAILY',
        defaultImpact: 'HIGH',
        budgetShare: 0.15,
        action: 'Check against drivers’ hours; investigate schedule pressure.',
    },
    IDLE: {
        label: 'Idling',
        description: 'Engine-running time without driving, well above this vehicle’s normal.',
        features: ['vehicle.daily.idleMinutes'],
        incidentType: 'ASSET_AVAILABILITY_RISK',
        latency: 'DAILY',
        defaultImpact: 'LOW',
        budgetShare: 0.1,
        action: 'Review for fuel waste, delays on site, or an engine left running.',
    },
    ACTIVITY: {
        label: 'Activity',
        description: 'A normally busy vehicle that has become unexpectedly still.',
        features: ['vehicle.daily.tripCount', 'vehicle.daily.ignitionOnMinutes'],
        incidentType: 'ASSET_AVAILABILITY_RISK',
        latency: 'DAILY',
        defaultImpact: 'MEDIUM',
        budgetShare: 0.15,
        action: 'Confirm off-hire, workshop or seasonal standing; check utilisation.',
    },
    CONNECTIVITY: {
        label: 'Connectivity',
        description: 'A collapse in the number of readings a device sends.',
        features: ['vehicle.daily.messageCount'],
        /*
         * ⚠️ VEHICLE_HEALTH_RISK rather than availability: the vehicle may be
         * working perfectly and its DEVICE has stopped. Filing it as availability
         * would put a healthy lorry in the same queue as one off the road, and
         * distort the metric that counts vehicles a fleet cannot use.
         */
        incidentType: 'VEHICLE_HEALTH_RISK',
        latency: 'DAILY',
        defaultImpact: 'HIGH',
        budgetShare: 0.25,
        action: 'Check device power and SIM before dispatching an engineer.',
    },
};
export const ANOMALY_FAMILY_KEYS = Object.keys(ANOMALY_FAMILIES);
/** The widened reading surface, for the reason `anomalyFeature` exists. */
const BY_KEY = ANOMALY_FAMILIES;
export function anomalyFamily(key) {
    return BY_KEY[key];
}
/**
 * What a tenant must hold for this family to produce anything.
 *
 * ⚠️ **DERIVED from the features it reads, never declared beside it.** A
 * hand-written entitlement drifts from what the family actually consumes, and
 * the drift is silent: an unentitled parameter is dropped at ingestion, so the
 * feature reads empty and the family looks like a fleet with nothing unusual
 * about it. Every family additionally requires `ANOMALY_DETECTION` itself —
 * which the key does NOT imply, so both must be held.
 */
export function anomalyFamilyEntitlements(key) {
    const needed = new Set(['ANOMALY_DETECTION']);
    for (const f of anomalyFamily(key).features)
        needed.add(anomalyFeatureEntitlement(f));
    return [...needed];
}
/**
 * Why a family cannot run for this tenant, or null where it can.
 *
 * ⚠️ **A REASON, not a boolean.** A tenant holding the anomaly key without
 * telemetry must see *"requires telemetry"* rather than an empty screen — an
 * absence and a missing entitlement look identical, and only one of them is
 * something they can act on. This is the more likely of the two cases, not the
 * rarer one.
 */
export function anomalyFamilyUnavailableReason(key, held) {
    const missing = anomalyFamilyEntitlements(key).filter((f) => !held.includes(f));
    if (missing.length === 0)
        return null;
    return missing.includes('ANOMALY_DETECTION')
        ? 'Anomaly detection is not included in your plan'
        : `Requires ${missing.join(' and ')}`;
}
/**
 * Which families read a feature.
 *
 * ⚠️ **A LIST, not a lookup, even though the six Release 1 families read
 * disjoint features today.** Nothing in the catalogue prevents two families
 * reading one measurement, and the design catalogues fifteen over a smaller set
 * of inputs. Written as a single lookup it would silently drop the second family
 * the day that happens — and the symptom is a whole family that never appears,
 * indistinguishable from a quiet fleet.
 *
 * ⚠️ Declared HERE rather than in `@oip/anomaly-engine`, so the emitter and the
 * screen answer "which family is this?" the same way. They were briefly two
 * copies.
 */
export function familiesReadingFeature(featureId) {
    return ANOMALY_FAMILY_KEYS.filter((k) => anomalyFamily(k).features.includes(featureId));
}
/** Every feature any shipped family reads — the engine's work list. */
export function familyFeatureIds() {
    const out = new Set();
    for (const k of ANOMALY_FAMILY_KEYS)
        for (const f of anomalyFamily(k).features)
            out.add(f);
    return [...out].sort();
}
/** Re-exported so a guard can reach a family's features' definitions. */
export { anomalyFeature };
/**
 * Which kinds of anomaly a tenant does NOT want raised.
 *
 * ⚠️ **MUTED is stored, never ENABLED, and this is the decision the feature
 * turns on.** `ANOMALY_FAMILIES` is a registry that GROWS — the design names
 * families for position, CAN and tachograph inputs that no detector reads yet —
 * so storing the set to RAISE would permanently opt a tenant out of everything
 * added afterwards, **silently**, because a list that looks deliberately curated
 * gives nobody a reason to wonder what is missing. Storing the muted set inverts
 * it: a family added next year is in nobody's muted set. Exactly the rule the
 * copilot's question deck already follows, and the notification registry before
 * it.
 */
export const ANOMALY_MUTED_FAMILIES_KEY = 'anomaly.mutedFamilies';
/** `"IDLE,ACTIVITY"` → the keys. Unknown names are DROPPED, never guessed. */
export function parseMutedFamilies(raw) {
    if (!raw)
        return [];
    const known = new Set(ANOMALY_FAMILY_KEYS);
    return [
        ...new Set(raw
            .split(',')
            .map((s) => s.trim())
            .filter((s) => known.has(s))),
    ];
}
export function formatMutedFamilies(keys) {
    return [...new Set(keys)].sort().join(',');
}
//# sourceMappingURL=anomaly-families.js.map